import { useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import { FcSearch } from 'react-icons/fc';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Autocomplete from '@mui/material/Autocomplete';
import { ProductoFiltroContainer } from './productosComponents';
import { UserContext } from '../../context/UserContext';
import { Button, Select, MenuItem } from '@mui/material';
import { ProductContext } from '../../context/ProductContext';
import debounce from 'lodash/debounce';

function Filtros({ classes, currentUser, setCurrentUser, setToggleCleared, setUserManuallySelected }) {
  // Context
  const { users } = useContext(UserContext);
  const { updateFilterState, filterState, setFilterState } = useContext(ProductContext);

  // Debounced text search
  const debouncedTextSearch = useCallback(
    debounce((value) => {
      updateFilterState("text", value);
    }, 300),
    []
  );

  // Debounced marca search
  const debouncedMarcaSearch = useCallback(
    debounce((value) => {
      updateFilterState("marca", value);
    }, 300),
    []
  );

  function limpiar() {
    setFilterState({
      userSearch: "",
      marca: "",
      estado: "CUALQUIERA",
      text: "",
      dateFrom: "",
      dateTo: "",
      codigo: "",
      display: 10,
      page: 1,
      omitProductSelection: false
    });
    setCurrentUser(null);
    setUserManuallySelected(false); // Reset manual selection flag
    setToggleCleared(prev => !prev);
  }

  const handleDateChange = (field, value) => {
    if (field === 'dateFrom' && filterState.dateTo && value > filterState.dateTo) {
      // If from date is after to date, update both
      setFilterState(prev => ({
        ...prev,
        dateFrom: value,
        dateTo: value
      }));
    } else {
      updateFilterState(field, value);
    }
  };

  useEffect(() => {
    if (currentUser) {
      setFilterState(prev => ({ ...prev, UserId: currentUser.identificador, page: 1 }));
    } else {
      updateFilterState("UserId", null);
    }
  }, [currentUser]);

  return (
    <div>
      <ProductoFiltroContainer>
        <TextField
          style={{flex: 1}}
          variant='outlined'
          label='Buscar por descripcion o palabra clave'
          id='Buscar'
          value={filterState.text}
          onChange={e => {
            const value = e.target.value;
            setFilterState(prev => ({ ...prev, text: value, page: 1 }));
            debouncedTextSearch(value);
          }}
          InputProps={{
            endAdornment: <FcSearch />,
          }}
        />
        <TextField
          style={{flex: 1}}
          variant='outlined'
          label='Buscar por marca'
          id='BuscarMarca'
          value={filterState.marca}
          onChange={e => {
            const value = e.target.value;
            setFilterState(prev => ({ ...prev, marca: value, page: 1 }));
            debouncedMarcaSearch(value);
          }}
          InputProps={{
            endAdornment: <FcSearch />,
          }}
        />
        <Autocomplete
          style={{flex: 1}}
          id='combo-box-demo'
          options={users}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          inputValue={filterState.userSearch}
          value={currentUser}
          onInputChange={(_, newInputValue) => {
            updateFilterState("userSearch", newInputValue);
            setFilterState(prev => ({ ...prev, page: 1 }));
          }}
          onChange={(_, value, reason) => {
            if (reason === "selectOption") {
              setCurrentUser(value);
              setUserManuallySelected(true); // Mark as manually selected
              updateFilterState("UserId", value.identificador);
            } else {
              setCurrentUser(null);
              setUserManuallySelected(false); // Clear manual selection
              updateFilterState("UserId", null);
            }
            setToggleCleared(prev => !prev);
          }}
          getOptionLabel={option => option.nombreCompleto}
          renderOption={(props, option) => {
            return <li {...props} key={option._id}>{option.nombreCompleto}</li>;
          }}
          renderInput={params => <TextField {...params} label='Elija un proveedor' variant='outlined' />}
        />
      </ProductoFiltroContainer>
      <ProductoFiltroContainer>
        <TextField
          style={{flex: 1}}
          variant='outlined'
          id='date'
          label='desde'
          type='date'
          value={filterState.dateFrom}
          onChange={e => {
            handleDateChange('dateFrom', e.target.value);
            setFilterState(prev => ({ ...prev, page: 1 }));
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          style={{flex: 1}}
          variant='outlined'
          id='date'
          label='hasta'
          type='date'
          inputProps={{
            min: filterState.dateFrom,
          }}
          value={filterState.dateTo}
          onChange={e => {
            handleDateChange('dateTo', e.target.value);
            setFilterState(prev => ({ ...prev, page: 1 }));
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <FormControl className={classes.formControl}>
          <InputLabel htmlFor='estado'>Estado</InputLabel>
          <Select 
            className={classes.select} 
            variant="outlined" 
            value={filterState.estado}
            onChange={e => {
              const value = e.target.value;
              updateFilterState("estado", value === 'CUALQUIERA' ? "" : value);
              setFilterState(prev => ({ ...prev, page: 1 }));
            }} 
            label='Estado'
          >
            <MenuItem value='CUALQUIERA'>CUALQUIERA</MenuItem>
            <MenuItem value='STOCK'>STOCK</MenuItem>
            <MenuItem value='VENDIDO'>VENDIDO</MenuItem>
            <MenuItem value='RESERVADO'>RESERVADO</MenuItem>
            <MenuItem value='OTRO'>OTRO</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={classes.formControl}>
          <InputLabel htmlFor='temporada'>Temporada</InputLabel>
          <Select 
            className={classes.select} 
            variant="outlined" 
            value={filterState.temporada}
            onChange={e => {
              const value = e.target.value;
              updateFilterState("temporada", value === 'TODAS' ? "" : value);
              setFilterState(prev => ({ ...prev, page: 1 }));
            }} 
            label='Temporada'
          >
            <MenuItem value='TODAS'>TODAS</MenuItem>
            <MenuItem value='OTOÑO/INVIERNO'>OTOÑO/INVIERNO</MenuItem>
            <MenuItem value='PRIMAVERA/VERANO'>PRIMAVERA/VERANO</MenuItem>
          </Select>
        </FormControl>
        <TextField
          style={{flex: 1}}
          variant='outlined'
          id='codigo'
          label='Codigo'
          value={filterState.codigo}
          onChange={e => {
            updateFilterState("codigo", e.target.value);
            setFilterState(prev => ({ ...prev, page: 1 }));
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <Button
          style={{ width: 100, height: 55, flex: 1 }}
          onClick={limpiar}
          variant='contained'
          color='primary'
        >
          Limpiar Filtros
        </Button>
      </ProductoFiltroContainer>
    </div>
  );
}

Filtros.propTypes = {
  classes: PropTypes.any,
  currentUser: PropTypes.object,
  setCurrentUser: PropTypes.func,
  params: PropTypes.object,
  setToggleCleared: PropTypes.func,
  setUserManuallySelected: PropTypes.func,
};

export default Filtros;
